const keyOf = value => String(value)

const organizationById = organizations => new Map(
  organizations
    .filter(group => group?.id !== undefined && group?.id !== null)
    .map(group => [keyOf(group.id), group]),
)

const isDescendantOf = (candidateId, ancestorId, groupsById) => {
  const ancestorKey = keyOf(ancestorId)
  let current = groupsById.get(keyOf(candidateId))
  const visited = new Set([keyOf(candidateId)])

  while (current?.parent_id !== undefined && current?.parent_id !== null) {
    const parentKey = keyOf(current.parent_id)
    if (parentKey === ancestorKey) return true
    if (visited.has(parentKey)) return false
    visited.add(parentKey)
    current = groupsById.get(parentKey)
    if (!current) return false
  }
  return false
}

export function normalizePermissionOrganization(row) {
  if (!row || row.id === undefined || row.id === null) return null
  const name = row.name || `Group #${row.id}`
  const numericDepth = Number(row.depth)
  const depth = Number.isInteger(numericDepth) && numericDepth >= 0 ? numericDepth : 0

  return {
    ...row,
    parent_id: row.parent_id ?? null,
    depth,
    path: Array.isArray(row.path) && row.path.length ? [...row.path] : [name],
    is_project_scope_root: row.is_project_scope_root === true,
  }
}

export function groupPermissionLabel(group) {
  const path = Array.isArray(group?.path) ? group.path.filter(Boolean) : []
  return path.join(' > ') || group?.name || group?.text || String(group?.id ?? '-')
}

export function filterPermissionOrganizations(items = [], query = '') {
  const keyword = String(query).trim().toLowerCase()
  if (!keyword) return items

  return items.filter(item => [
    item?.name,
    item?.text,
    item?.organization_path_text,
    Array.isArray(item?.path) ? item.path.join(' > ') : '',
  ].some(value => String(value || '').toLowerCase().includes(keyword)))
}

export function permissionOrganizationIndent(group) {
  return Math.min(6, Math.max(0, Number(group?.depth) || 0))
}

export function availablePermissionGroups({ organizations = [], selectedGroupIds = [] } = {}) {
  const groupsById = organizationById(organizations)
  const selectedKeys = new Set(selectedGroupIds.map(keyOf))

  return organizations.filter(group => {
    const groupKey = keyOf(group.id)
    if (selectedKeys.has(groupKey)) return false
    return !selectedGroupIds.some(selectedId => (
      groupsById.has(keyOf(selectedId))
      && isDescendantOf(group.id, selectedId, groupsById)
    ))
  })
}

export function addPermissionGroupSelection({
  organizations = [],
  selectedGroupIds = [],
  groupId,
} = {}) {
  if (groupId === undefined || groupId === null || groupId === '') return [...selectedGroupIds]
  const groupsById = organizationById(organizations)
  if (selectedGroupIds.some(id => keyOf(id) === keyOf(groupId))) return [...selectedGroupIds]
  if (selectedGroupIds.some(selectedId => (
    groupsById.has(keyOf(selectedId))
    && isDescendantOf(groupId, selectedId, groupsById)
  ))) return [...selectedGroupIds]

  const retainedIds = selectedGroupIds.filter(selectedId => (
    !groupsById.has(keyOf(selectedId))
    || !isDescendantOf(selectedId, groupId, groupsById)
  ))
  return [...retainedIds, groupId]
}
